import { useEffect, useState, useCallback } from 'react';
import { Card, StatCard, Badge, Spinner, Empty } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, FormField, Label } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../contexts/ToastContext';
import {
  adminApi,
  type AdminStats,
  type AdminCommission,
  type PendingPayout,
  type KycQueueRow,
  type AuditLog,
} from '../../api/admin';
import { ApiError, tokens } from '../../api/client';
import { fmtUsdt, fmtAiml, fmtDate, shortenAddress } from '../../lib/format';

type Tab = 'stats' | 'kyc' | 'commissions' | 'payouts' | 'audit';

export default function AdminConsole() {
  const [tab, setTab] = useState<Tab>('stats');

  return (
    <div className="max-w-7xl mx-auto py-8 px-4">
      <div className="mb-6">
        <div className="eyebrow text-magenta-500 mb-2">Admin console</div>
        <h1 className="font-display text-4xl font-bold">Operations</h1>
        <p className="text-ink-600 mt-2">
          Manage KYC, commissions, payouts, and audit trail.
        </p>
      </div>

      {/* Tab nav */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-paper-300">
        {(
          [
            ['stats', 'Stats'],
            ['kyc', 'KYC queue'],
            ['commissions', 'Commissions'],
            ['payouts', 'Payouts'],
            ['audit', 'Audit log'],
          ] as Array<[Tab, string]>
        ).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition ${
              tab === id
                ? 'border-violet-500 text-violet-600'
                : 'border-transparent text-ink-600 hover:text-ink hover:border-paper-400'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div>
        {tab === 'stats' && <StatsTab />}
        {tab === 'kyc' && <KycTab />}
        {tab === 'commissions' && <CommissionsTab />}
        {tab === 'payouts' && <PayoutsTab />}
        {tab === 'audit' && <AuditTab />}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * STATS
 * ──────────────────────────────────────────────────────────────────────── */
function StatsTab() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminApi
      .stats()
      .then(setStats)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner size={32} />;
  if (error || !stats) return <Card><span className="text-danger">{error}</span></Card>;

  // Commission status totals
  const commStatuses = Object.entries(stats.commissions);

  return (
    <div className="space-y-6">
      {/* Users */}
      <div>
        <h2 className="eyebrow mb-3">Users</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total" value={stats.users.total} accent="violet" />
          <StatCard label="New / 7d" value={stats.users.newLast7d} accent="azure" />
          <StatCard
            label="KYC pending"
            value={stats.users.kyc.pending}
            accent="magenta"
          />
          <StatCard label="KYC approved" value={stats.users.kyc.approved} accent="ink" />
        </div>
      </div>

      {/* Presale */}
      <div>
        <h2 className="eyebrow mb-3">Presale</h2>
        <div className="grid grid-cols-2 gap-4">
          <StatCard
            label="USDT raised"
            value={`$${fmtUsdt(stats.presale.totalRaisedUsdt)}`}
            accent="magenta"
          />
          <StatCard
            label="Purchases"
            value={stats.presale.totalPurchaseCount}
            accent="violet"
          />
        </div>
      </div>

      {/* Commissions by status */}
      <div>
        <h2 className="eyebrow mb-3">Commissions by status</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {commStatuses.length === 0 && (
            <Card><span className="text-ink-600 text-sm">No commissions yet.</span></Card>
          )}
          {commStatuses.map(([status, data]) => (
            <Card key={status}>
              <div className="eyebrow mb-1">{status}</div>
              <div className="font-mono text-2xl font-bold">${fmtUsdt(data.sumUsdt)}</div>
              <div className="text-xs text-ink-600 mt-1">
                {data.count} commission{data.count === 1 ? '' : 's'}
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Bonuses */}
      <div>
        <h2 className="eyebrow mb-3">AIML bonus program</h2>
        <Card>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <div className="eyebrow mb-1">Total awarded</div>
              <div className="font-mono text-2xl font-bold">
                {fmtAiml(stats.bonuses.totalAwardedAiml)}
              </div>
              <div className="text-xs text-ink-600 mt-1">AIML tokens</div>
            </div>
            <div>
              <div className="eyebrow mb-1">Program budget</div>
              <div className="font-mono text-2xl font-bold">
                {fmtAiml(stats.bonuses.programBudgetAiml)}
              </div>
              <div className="text-xs text-ink-600 mt-1">AIML tokens</div>
            </div>
            <div>
              <div className="eyebrow mb-1">Budget used</div>
              <div className="font-mono text-2xl font-bold">
                {stats.bonuses.percentOfBudgetUsed}%
              </div>
              <div className="text-xs text-ink-600 mt-1">of allocation</div>
            </div>
          </div>
          <div>
            <div className="eyebrow mb-2">Breakdown by kind</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(stats.bonuses.breakdown).map(([kind, data]) => (
                <div
                  key={kind}
                  className="bg-paper-100 px-3 py-2 rounded-xl border border-paper-200"
                >
                  <div className="text-xs uppercase tracking-wider text-ink-600">{kind}</div>
                  <div className="font-mono text-sm font-bold">{fmtAiml(data.sumAiml)} AIML</div>
                  <div className="text-xs text-ink-600">{data.count} awards</div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      {/* Indexer */}
      {stats.indexer && (
        <Card>
          <div className="eyebrow mb-2">Polygon indexer</div>
          <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
            <div>
              <span className="text-ink-600">Last block scanned:</span>{' '}
              <span className="font-mono font-semibold">{stats.indexer.lastBlock}</span>
            </div>
            <div>
              <span className="text-ink-600">Updated:</span>{' '}
              <span className="font-mono">{fmtDate(stats.indexer.updatedAt)}</span>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * KYC QUEUE
 * ──────────────────────────────────────────────────────────────────────── */
function KycTab() {
  const toast = useToast();
  const [queue, setQueue] = useState<KycQueueRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    adminApi
      .kycQueue()
      .then((r) => setQueue(r.submissions))
      .catch(() => setQueue([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (loading) return <Spinner size={32} />;
  if (!queue || queue.length === 0) {
    return <Empty title="No pending KYC submissions" hint="Queue is clear." />;
  }

  return (
    <>
      <Card className="!p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-paper-100">
            <tr className="text-left">
              <th className="px-4 py-3 eyebrow">Email</th>
              <th className="px-4 py-3 eyebrow">Name</th>
              <th className="px-4 py-3 eyebrow">Country</th>
              <th className="px-4 py-3 eyebrow">Doc</th>
              <th className="px-4 py-3 eyebrow">Submitted</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {queue.map((row) => (
              <tr key={row.id} className="border-t border-paper-200">
                <td className="px-4 py-3 font-mono text-xs">{row.email}</td>
                <td className="px-4 py-3">{row.fullName}</td>
                <td className="px-4 py-3 font-mono">{row.countryCode}</td>
                <td className="px-4 py-3 font-mono text-xs">{row.documentType}</td>
                <td className="px-4 py-3 text-ink-700">{fmtDate(row.submittedAt)}</td>
                <td className="px-4 py-3 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedId(row.id)}
                  >
                    Review
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {selectedId && (
        <KycReviewModal
          submissionId={selectedId}
          onClose={() => setSelectedId(null)}
          onDecided={() => {
            toast.success('Decision recorded');
            setSelectedId(null);
            refresh();
          }}
        />
      )}
    </>
  );
}

function KycFilePreview({
  submissionId,
  kind,
  label,
}: {
  submissionId: string;
  kind: 'document' | 'selfie' | 'tax-document';
  label: string;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [contentType, setContentType] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    const load = async () => {
      try {
        const access = tokens.getAccess();
        if (!access) throw new Error('Not authenticated');
        const res = await fetch(`/api/admin/kyc/${submissionId}/${kind}`, {
          headers: { Authorization: `Bearer ${access}` },
        });
        if (!res.ok) {
          if (res.status === 404) throw new Error('File not present');
          throw new Error(`HTTP ${res.status}`);
        }
        const ct = res.headers.get('Content-Type') || '';
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
        setContentType(ct);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load');
        }
      }
    };
    load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [submissionId, kind]);

  const isPdf = contentType === 'application/pdf';

  return (
    <div>
      <div className="text-xs text-ink-600 mb-1 uppercase tracking-wide">{label}</div>
      <div className="aspect-square bg-paper-100 rounded-2xl overflow-hidden flex items-center justify-center border border-paper-300">
        {error && (
          <div className="text-danger text-xs p-4 text-center">
            <div className="font-semibold">Failed to load</div>
            <div className="opacity-60 text-[10px] mt-1">{error}</div>
          </div>
        )}
        {!error && !src && (
          <div className="text-ink-600 text-xs">Loading…</div>
        )}
        {!error && src && !isPdf && (
          <img
            src={src}
            alt={label}
            className="w-full h-full object-contain"
          />
        )}
        {!error && src && isPdf && (
          <div className="text-center p-4">
            <div className="text-4xl mb-2">📄</div>
            <a
              href={src}
              target="_blank"
              rel="noopener noreferrer"
              className="text-violet-600 text-sm hover:underline font-semibold"
            >
              Open PDF
            </a>
            <p className="text-xs text-ink-600 mt-1">in new tab</p>
          </div>
        )}
      </div>
      {!error && src && !isPdf && (
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-violet-600 hover:underline mt-1 inline-block"
        >
          View full size →
        </a>
      )}
    </div>
  );
}

function KycReviewModal({
  submissionId,
  onClose,
  onDecided,
}: {
  submissionId: string;
  onClose: () => void;
  onDecided: () => void;
}) {
  interface KycDetail {
    id: string;
    userId: string;
    email: string;
    status: string;
    submittedAt: string;
    decidedAt: string | null;
    fullName: string;
    dateOfBirth: string;
    mobileNumber: string;
    countryCode: string;
    countryOther: string | null;
    documentType: string;
    documentNumber: string | null;
    documentNumberMasked: string | null;
    taxIdNumber: string | null;
    taxIdMasked: string | null;
    hasTaxDocument: boolean;
    rejectionReason: string | null;
    adminNotes: string | null;
  }

  const [detail, setDetail] = useState<KycDetail | null>(null);
  const [notes, setNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  useEffect(() => {
    adminApi
      .kycDetail(submissionId)
      .then((d) => setDetail(d as unknown as KycDetail))
      .catch(() => onClose());
  }, [submissionId, onClose]);

  const handleApprove = async () => {
    setBusy(true);
    try {
      await adminApi.kycApprove(submissionId, notes || undefined);
      onDecided();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Approve failed');
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setBusy(true);
    try {
      await adminApi.kycReject(submissionId, rejectReason);
      onDecided();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Reject failed');
    } finally {
      setBusy(false);
    }
  };

  // Resolve country: if user picked OTHER, show the country they typed in
  const countryDisplay = detail
    ? detail.countryCode === 'OTHER'
      ? detail.countryOther || 'Other (unspecified)'
      : detail.countryCode || '—'
    : '—';

  return (
    <Modal open onClose={onClose} title="Review KYC submission" maxWidth="lg">
      {!detail ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-5">
          {/* Identity details */}
          <div>
            <div className="eyebrow mb-2">Identity details</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm bg-paper-100 p-4 rounded-2xl">
              <div>
                <div className="text-xs text-ink-600 uppercase tracking-wide">Email</div>
                <div className="font-mono text-xs mt-0.5 break-all">{detail.email || '—'}</div>
              </div>
              <div>
                <div className="text-xs text-ink-600 uppercase tracking-wide">Full name</div>
                <div className="mt-0.5 font-semibold">{detail.fullName || '—'}</div>
              </div>
              <div>
                <div className="text-xs text-ink-600 uppercase tracking-wide">Date of birth</div>
                <div className="font-mono mt-0.5">{detail.dateOfBirth || '—'}</div>
              </div>
              <div>
                <div className="text-xs text-ink-600 uppercase tracking-wide">Mobile</div>
                <div className="font-mono mt-0.5 break-all">{detail.mobileNumber || '—'}</div>
              </div>
              <div>
                <div className="text-xs text-ink-600 uppercase tracking-wide">Country</div>
                <div className="font-mono mt-0.5">{countryDisplay}</div>
              </div>
              <div>
                <div className="text-xs text-ink-600 uppercase tracking-wide">Document type</div>
                <div className="font-mono mt-0.5">{detail.documentType}</div>
              </div>
              <div>
                <div className="text-xs text-ink-600 uppercase tracking-wide">Document number</div>
                <div className="font-mono mt-0.5 break-all">
                  {detail.documentNumber || detail.documentNumberMasked || '—'}
                </div>
              </div>
              {(detail.taxIdNumber || detail.taxIdMasked) && (
                <div>
                  <div className="text-xs text-ink-600 uppercase tracking-wide">Tax ID</div>
                  <div className="font-mono mt-0.5 break-all">
                    {detail.taxIdNumber || detail.taxIdMasked}
                  </div>
                </div>
              )}
              {detail.hasTaxDocument && (
                <div>
                  <div className="text-xs text-ink-600 uppercase tracking-wide">Tax document</div>
                  <div className="text-success font-semibold mt-0.5">✓ Uploaded</div>
                </div>
              )}
            </div>
          </div>

          {/* Document images */}
          <div>
            <div className="eyebrow mb-2">Submitted documents</div>
            <div className="grid grid-cols-2 gap-3">
              <KycFilePreview submissionId={detail.id} kind="document" label="ID document" />
              <KycFilePreview submissionId={detail.id} kind="selfie" label="Selfie" />
            </div>
            {detail.hasTaxDocument && (
              <div className="mt-3 max-w-xs">
                <KycFilePreview
                  submissionId={detail.id}
                  kind="tax-document"
                  label="Tax document"
                />
              </div>
            )}
          </div>

          {/* Reviewer fields */}
          <FormField label="Approval notes (optional)" htmlFor="notes">
            <Input
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder='e.g. "Verified against passport scan"'
            />
          </FormField>

          <FormField label="Rejection reason (required to reject)" htmlFor="reject">
            <Input
              id="reject"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder='e.g. "ID image too blurry to verify"'
            />
          </FormField>

          <div className="flex gap-3 justify-end pt-4 border-t border-paper-200">
            <Button variant="danger" onClick={handleReject} isLoading={busy}>
              Reject
            </Button>
            <Button variant="gradient" onClick={handleApprove} isLoading={busy}>
              Approve
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * COMMISSIONS
 * ──────────────────────────────────────────────────────────────────────── */
function CommissionsTab() {
  const toast = useToast();
  const [list, setList] = useState<AdminCommission[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [voidTarget, setVoidTarget] = useState<AdminCommission | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    adminApi
      .commissions({ status: statusFilter || undefined, limit: 100 })
      .then((r) => setList(r.commissions))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Label htmlFor="status">Filter by status</Label>
        <select
          id="status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-xl border border-paper-300 bg-white text-sm"
        >
          <option value="">All</option>
          <option value="ACCRUED">ACCRUED</option>
          <option value="PAYABLE">PAYABLE</option>
          <option value="PAID">PAID</option>
          <option value="VOID">VOID</option>
        </select>
        <Button onClick={refresh} variant="ghost" size="sm">
          Refresh
        </Button>
      </div>

      {loading ? (
        <Spinner size={32} />
      ) : !list || list.length === 0 ? (
        <Empty title="No commissions match" />
      ) : (
        <Card className="!p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-paper-100">
                <tr className="text-left">
                  <th className="px-3 py-3 eyebrow">Earned</th>
                  <th className="px-3 py-3 eyebrow">Status</th>
                  <th className="px-3 py-3 eyebrow">Referrer</th>
                  <th className="px-3 py-3 eyebrow">Referee</th>
                  <th className="px-3 py-3 eyebrow text-right">Purchase</th>
                  <th className="px-3 py-3 eyebrow text-right">Commission</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.id} className="border-t border-paper-200">
                    <td className="px-3 py-3 text-ink-700 text-xs">{fmtDate(c.earnedAt)}</td>
                    <td className="px-3 py-3">
                      <Badge
                        kind={
                          c.status === 'PAID'
                            ? 'success'
                            : c.status === 'PAYABLE'
                              ? 'info'
                              : c.status === 'VOID'
                                ? 'danger'
                                : 'neutral'
                        }
                      >
                        {c.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-3 font-mono text-xs">
                      {c.referrer.email}
                      <div className="text-ink-600">KYC: {c.referrer.kycStatus}</div>
                    </td>
                    <td className="px-3 py-3 font-mono text-xs">
                      {c.referee?.email ?? '—'}
                    </td>
                    <td className="px-3 py-3 text-right font-mono">
                      ${fmtUsdt(c.purchase.usdtAmount)}
                    </td>
                    <td className="px-3 py-3 text-right font-mono font-semibold">
                      ${fmtUsdt(c.usdtAmount)}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {(c.status === 'ACCRUED' || c.status === 'PAYABLE') && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setVoidTarget(c)}
                        >
                          Void
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {voidTarget && (
        <VoidCommissionModal
          commission={voidTarget}
          onClose={() => setVoidTarget(null)}
          onDone={() => {
            toast.success('Commission voided');
            setVoidTarget(null);
            refresh();
          }}
        />
      )}
    </>
  );
}

function VoidCommissionModal({
  commission,
  onClose,
  onDone,
}: {
  commission: AdminCommission;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const submit = async () => {
    if (!reason.trim()) {
      toast.error('Reason is required');
      return;
    }
    setBusy(true);
    try {
      await adminApi.voidCommission(commission.id, reason);
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Failed to void');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Void commission">
      <div className="space-y-4">
        <div className="bg-paper-100 p-3 rounded-2xl text-sm">
          <div>
            Referrer:{' '}
            <span className="font-mono">{commission.referrer.email}</span>
          </div>
          <div>
            Amount:{' '}
            <span className="font-mono font-bold">
              ${fmtUsdt(commission.usdtAmount)}
            </span>
          </div>
          <div>
            Status: <Badge kind="neutral">{commission.status}</Badge>
          </div>
        </div>
        <FormField label="Void reason (required)" htmlFor="reason">
          <Input
            id="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Self-referral abuse, chargeback, etc."
            required
          />
        </FormField>
        <p className="text-xs text-ink-600">
          Voiding is permanent and will be recorded in the audit log. The referrer's
          payable balance will be reduced accordingly.
        </p>
        <div className="flex gap-3 justify-end">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="danger" onClick={submit} isLoading={busy}>
            Confirm void
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * PAYOUTS
 * ──────────────────────────────────────────────────────────────────────── */
function PayoutsTab() {
  const toast = useToast();
  const [pending, setPending] = useState<PendingPayout[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<PendingPayout | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    adminApi
      .pendingPayouts()
      .then((r) => setPending(r.pending))
      .catch(() => setPending([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold">Pending payouts</h2>
          <p className="text-ink-600 text-sm mt-1">
            Users with KYC + linked wallet + payable balance
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={refresh}>Refresh</Button>
      </div>

      {loading ? (
        <Spinner size={32} />
      ) : !pending || pending.length === 0 ? (
        <Empty title="No pending payouts" hint="All eligible users have been paid." />
      ) : (
        <Card className="!p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-paper-100">
              <tr className="text-left">
                <th className="px-4 py-3 eyebrow">Email</th>
                <th className="px-4 py-3 eyebrow">Wallet</th>
                <th className="px-4 py-3 eyebrow text-right">Payable</th>
                <th className="px-4 py-3 eyebrow text-right">Commissions</th>
                <th className="px-4 py-3 eyebrow text-right">AIML bonus</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {pending.map((p) => (
                <tr key={p.userId} className="border-t border-paper-200">
                  <td className="px-4 py-3 font-mono text-xs">{p.email}</td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {shortenAddress(p.walletAddress)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-bold">
                    ${fmtUsdt(p.payableUsdt)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{p.commissionCount}</td>
                  <td className="px-4 py-3 text-right font-mono">
                    {fmtAiml(p.aimlBonusAccrued)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      size="sm"
                      variant="gradient"
                      onClick={() => setSelected(p)}
                    >
                      Process payout
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {selected && (
        <PayoutModal
          row={selected}
          onClose={() => setSelected(null)}
          onDone={() => {
            toast.success('Payout recorded');
            setSelected(null);
            refresh();
          }}
        />
      )}
    </>
  );
}

function PayoutModal({
  row,
  onClose,
  onDone,
}: {
  row: PendingPayout;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [commissions, setCommissions] = useState<AdminCommission[] | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [txHash, setTxHash] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminApi
      .userCommissions(row.userId)
      .then((r) => {
        setCommissions(r.commissions);
        // Pre-select all PAYABLE
        setSelectedIds(new Set(r.commissions.filter((c) => c.status === 'PAYABLE').map((c) => c.id)));
      })
      .catch(() => setCommissions([]));
  }, [row.userId]);

  const toggle = (id: string) => {
    setSelectedIds((curr) => {
      const next = new Set(curr);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totalSelected = (commissions ?? [])
    .filter((c) => selectedIds.has(c.id))
    .reduce((sum, c) => sum + c.usdtAmount, 0);

  const submit = async () => {
    if (selectedIds.size === 0) {
      toast.error('Select at least one commission');
      return;
    }
    if (!txHash.trim() || !txHash.startsWith('0x')) {
      toast.error('Provide a valid USDT transfer tx hash (starts with 0x)');
      return;
    }
    setBusy(true);
    try {
      await adminApi.createPayout({
        recipientUserId: row.userId,
        commissionIds: Array.from(selectedIds),
        txHash,
        notes: notes.trim() || undefined,
      });
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Payout failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={`Payout to ${row.email}`} maxWidth="lg">
      <div className="space-y-4">
        <div className="bg-paper-100 p-3 rounded-2xl text-sm">
          <div>
            Destination wallet:{' '}
            <span className="font-mono">{row.walletAddress}</span>
          </div>
          <div>
            KYC: <Badge kind="success">{row.kycStatus}</Badge>
          </div>
        </div>

        <div>
          <div className="eyebrow mb-2">
            Select commissions to include · {selectedIds.size} selected, $
            {fmtUsdt(totalSelected)}
          </div>
          {!commissions ? (
            <Spinner size={20} />
          ) : (
            <div className="max-h-64 overflow-y-auto border border-paper-300 rounded-2xl">
              {commissions
                .filter((c) => c.status === 'PAYABLE' || c.status === 'PAID')
                .map((c) => (
                  <label
                    key={c.id}
                    className={`flex items-center gap-3 px-3 py-2 border-b border-paper-200 last:border-0 cursor-pointer hover:bg-paper-100 ${
                      c.status === 'PAID' ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(c.id)}
                      onChange={() => toggle(c.id)}
                      disabled={c.status === 'PAID'}
                    />
                    <div className="flex-1 text-xs">
                      <div className="font-mono">{fmtDate(c.earnedAt)}</div>
                      <div className="text-ink-600">
                        From {c.referee?.email ?? '—'} · ${fmtUsdt(c.purchase.usdtAmount)} purchase
                      </div>
                    </div>
                    <div className="font-mono font-bold">${fmtUsdt(c.usdtAmount)}</div>
                    <Badge
                      kind={c.status === 'PAID' ? 'success' : 'info'}
                    >
                      {c.status}
                    </Badge>
                  </label>
                ))}
            </div>
          )}
        </div>

        <FormField label="USDT transfer tx hash" htmlFor="txHash">
          <Input
            id="txHash"
            value={txHash}
            onChange={(e) => setTxHash(e.target.value)}
            placeholder="0x…"
            className="font-mono text-xs"
            required
          />
        </FormField>

        <FormField label="Notes (optional)" htmlFor="notes">
          <Input
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Weekly batch payout"
          />
        </FormField>

        <p className="text-xs text-ink-600">
          You must have already sent <span className="font-bold">${fmtUsdt(totalSelected)}</span>{' '}
          USDT (Polygon) to <span className="font-mono">{row.walletAddress}</span>. This form{' '}
          records the payment; it does not execute it.
        </p>

        <div className="flex gap-3 justify-end">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="gradient" onClick={submit} isLoading={busy}>
            Record payout
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
 * AUDIT LOG
 * ──────────────────────────────────────────────────────────────────────── */
function AuditTab() {
  const [logs, setLogs] = useState<AuditLog[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');

  const refresh = useCallback(() => {
    setLoading(true);
    adminApi
      .audit({ action: actionFilter || undefined, limit: 100 })
      .then((r) => setLogs(r.logs))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [actionFilter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Label htmlFor="action">Filter by action</Label>
        <select
          id="action"
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="px-3 py-2 rounded-xl border border-paper-300 bg-white text-sm"
        >
          <option value="">All</option>
          <option value="KYC_APPROVED">KYC_APPROVED</option>
          <option value="KYC_REJECTED">KYC_REJECTED</option>
          <option value="PAYOUT_RECORDED">PAYOUT_RECORDED</option>
          <option value="COMMISSION_VOIDED">COMMISSION_VOIDED</option>
        </select>
        <Button variant="ghost" size="sm" onClick={refresh}>Refresh</Button>
      </div>

      {loading ? (
        <Spinner size={32} />
      ) : !logs || logs.length === 0 ? (
        <Empty title="No audit entries" />
      ) : (
        <div className="space-y-2">
          {logs.map((log) => (
            <Card key={log.id} className="!p-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Badge kind="info">{log.action}</Badge>
                    <span className="text-xs text-ink-600">{log.targetType}</span>
                  </div>
                  <div className="text-sm text-ink-700 mt-2">
                    <span className="text-ink-600">By:</span>{' '}
                    <span className="font-mono">{log.adminEmail}</span>
                  </div>
                  <details className="mt-2">
                    <summary className="text-xs text-violet-600 cursor-pointer hover:underline">
                      Metadata
                    </summary>
                    <pre className="text-xs bg-paper-100 p-2 rounded-xl mt-1 overflow-x-auto font-mono">
                      {JSON.stringify(log.metadata, null, 2)}
                    </pre>
                  </details>
                </div>
                <div className="text-xs text-ink-600 font-mono whitespace-nowrap">
                  {fmtDate(log.createdAt)}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
